import { useState, useCallback, useRef, useEffect } from "react";
import type { TextareaHTMLAttributes, ChangeEvent } from "react";
import { MarkdownToolbar } from "./MarkdownToolbar";
import { MarkdownPreview } from "./MarkdownPreview";
import styles from "./MarkdownEditor.module.css";

export type ViewMode = "write" | "preview" | "split";

export interface MarkdownEditorProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange"
> {
  /** The markdown content value */
  value?: string;
  /** Default value for uncontrolled usage */
  defaultValue?: string;
  /** Called when the content changes */
  onChange?: (value: string) => void;
  /** Hint text shown below the editor */
  hint?: string;
  /** Error message to display */
  error?: string;
  /** Maximum character count (shows counter if provided) */
  maxLength?: number;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Whether to show the toolbar */
  showToolbar?: boolean;
  /** Whether to enable preview mode */
  enablePreview?: boolean;
  /** Initial view mode */
  initialViewMode?: ViewMode;
}

/**
 * A markdown editor component with formatting toolbar and live preview.
 * Supports both controlled and uncontrolled usage.
 */
export function MarkdownEditor({
  value: controlledValue,
  defaultValue = "",
  onChange,
  hint,
  error,
  maxLength,
  minHeight = 200,
  showToolbar = true,
  enablePreview = true,
  initialViewMode = "write",
  disabled,
  placeholder,
  name,
  id,
  className,
  ...textareaProps
}: MarkdownEditorProps) {
  // Support both controlled and uncontrolled modes
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled ? controlledValue : internalValue;

  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const insertMarkdown = useCallback(
    (before: string, after: string = "", defaultText: string = "") => {
      const textarea = textareaRef.current;
      if (!textarea || disabled) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end) || defaultText;
      const newValue =
        value.substring(0, start) + before + selectedText + after + value.substring(end);

      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);

      // Restore cursor position
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + before.length,
          start + before.length + selectedText.length,
        );
      });
    },
    [value, isControlled, onChange, disabled],
  );

  const handleFormat = useCallback(
    (format: string) => {
      switch (format) {
        case "bold":
          insertMarkdown("**", "**", "bold text");
          break;
        case "italic":
          insertMarkdown("_", "_", "italic text");
          break;
        case "strikethrough":
          insertMarkdown("~~", "~~", "strikethrough");
          break;
        case "heading1":
          insertMarkdown("# ", "", "Heading");
          break;
        case "heading2":
          insertMarkdown("## ", "", "Heading");
          break;
        case "heading3":
          insertMarkdown("### ", "", "Heading");
          break;
        case "link":
          insertMarkdown("[", "](url)", "link text");
          break;
        case "image":
          insertMarkdown("![", "](image-url)", "alt text");
          break;
        case "code":
          insertMarkdown("`", "`", "code");
          break;
        case "codeblock":
          insertMarkdown("```\n", "\n```", "code block");
          break;
        case "quote":
          insertMarkdown("> ", "", "quote");
          break;
        case "unorderedList":
          insertMarkdown("- ", "", "list item");
          break;
        case "orderedList":
          insertMarkdown("1. ", "", "list item");
          break;
        case "timestamp":
          insertMarkdown("[00:00] ", "", "description");
          break;
        case "hr":
          insertMarkdown("\n---\n", "");
          break;
      }
    },
    [insertMarkdown],
  );

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && viewMode !== "preview") {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
    }
  }, [value, viewMode, minHeight]);

  const characterCount = value.length;
  const isOverLimit = maxLength !== undefined && characterCount > maxLength;

  const containerClasses = [
    styles.container,
    error ? styles.hasError : "",
    disabled ? styles.disabled : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      {showToolbar && (
        <MarkdownToolbar
          onFormat={handleFormat}
          viewMode={viewMode}
          onViewModeChange={enablePreview ? setViewMode : undefined}
          disabled={disabled}
        />
      )}

      <div className={styles.editorBody}>
        {(viewMode === "write" || viewMode === "split") && (
          <div className={`${styles.writePane} ${viewMode === "split" ? styles.splitPane : ""}`}>
            <textarea
              ref={textareaRef}
              id={id}
              name={name}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              placeholder={placeholder}
              className={styles.textarea}
              style={{ minHeight: `${minHeight}px` }}
              {...textareaProps}
            />
          </div>
        )}

        {enablePreview && (viewMode === "preview" || viewMode === "split") && (
          <div className={`${styles.previewPane} ${viewMode === "split" ? styles.splitPane : ""}`}>
            <MarkdownPreview content={value} />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {hint && !error && <span className={styles.hint}>{hint}</span>}
          {error && <span className={styles.error}>{error}</span>}
        </div>
        <div className={styles.footerRight}>
          {maxLength !== undefined && (
            <span className={`${styles.charCount} ${isOverLimit ? styles.overLimit : ""}`}>
              {characterCount.toLocaleString()} / {maxLength.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
